/**
 * “Continue with Google” — the only way to sign in **and** to register.
 *
 * There is deliberately no password form anywhere on the web: a form is what
 * bots hammer (fake accounts, brute force, load). The button sends the browser
 * to the API (`/api/auth/google/start`), Google comes back to `/_oauth/google`
 * and the API signs in — or creates — the account from the **verified Google
 * email** (`Backend_API/routers/social_auth.py`). `OAuthHandler` stores the
 * session once the browser is back.
 *
 * `scope` is 'community' (global community account) or 'customer' (this shop,
 * needs `shopId`).
 */
import React, { useEffect, useState } from 'react';
import { googleLoginUrl, oauthConfig } from '../api';
import { useLanguage } from '../i18n';

/** Google's official mark. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.2-3.2-.5-4.7H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.3z" />
      <path fill="#FBBC05" d="M10.4 28.7c-.6-1.4-.9-2.9-.9-4.7s.3-3.3.9-4.7l-7.8-6.1C1 16.3 0 20 0 24s1 7.7 2.6 10.8l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.4 0-11.7-3.7-13.6-9l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}

export default function GoogleLogin({ scope = 'community', shopId = null, label }) {
  const { t } = useLanguage();
  const [enabled, setEnabled] = useState(null);   // null = still asking the API

  useEffect(() => {
    let alive = true;
    oauthConfig()
      .then((c) => { if (alive) setEnabled(!!c?.google?.enabled); })
      .catch(() => { if (alive) setEnabled(false); });
    return () => { alive = false; };
  }, []);

  if (enabled === null) {
    return (
      <div className="w-full h-12 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 animate-pulse" />
    );
  }

  if (!enabled) {
    return (
      <p className="text-xs text-center rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200">
        {t('googleNotReady')}
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={() => { window.location.href = googleLoginUrl({ scope, shopId }); }}
      className="w-full inline-flex items-center justify-center gap-2.5 py-3 rounded-xl border
                 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-bold
                 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
    >
      <GoogleMark /> {label || t('loginWithGoogle')}
    </button>
  );
}

